import { useEffect, useState } from "react"
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  IconButton,
} from "@mui/material"
import RichTextEditor from "../../components/reusableComponents/ResuableRichTextEditor"
import { useHistory } from "react-router-dom";
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import ReusableComponent from "../../components/reusableComponents/reusableInputComponents";
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ApiService from "../../_services/ApiService";
import { useParams } from "react-router-dom";
import { validateGuidelinesStep } from "../../_helpers/validationPage";
import { TrainRounded } from "@mui/icons-material";

const marginBottom = '13px';

const ContentGuidelines = ({ formData, setFormData, barterId, referenceLinks, setReferenceLinks, isEditMode, offerId, formErrors, setFormErrors }) => {
  const [linkEditedFlags, setLinkEditedFlags] = useState([]);
  const SectionTitle = ({ children }) => (
    <Typography sx={{ fontWeight: 500, fontSize: 18, color: '#626478', marginBottom: '10px' }}>
      {children}
    </Typography>
  );

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (formErrors?.[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };


  const handleAddReferenceLink = () => {
    if (referenceLinks.length < 4) {
      setReferenceLinks([...referenceLinks, { link: "" }]);
      setLinkEditedFlags((prev) => [...prev, false]);
    }
  };


  const handleRemoveReferenceLink = async (indexToRemove) => {
    const linkToDelete = referenceLinks[indexToRemove];

    if (isEditMode && linkToDelete?.id) {
      try {
        await ApiService.delete("reference_links", { id: linkToDelete.id });
        console.log("Reference link deleted successfully");
      } catch (error) {
        console.error("Failed to delete reference link:", error);
        return;
      }
    }

    setReferenceLinks((prev) => prev.filter((_, index) => index !== indexToRemove));
    setLinkEditedFlags((prev) => prev.filter((_, index) => index !== indexToRemove));
  };


  const handleReferenceLinkChange = (index, value) => {
    const updated = [...referenceLinks];
    updated[index] = { ...updated[index], link: value };

    setReferenceLinks(updated);
    setFormData((prev) => ({
      ...prev,
      reference_links: updated,
    }));

    setLinkEditedFlags((prev) => {
      const copy = [...prev];
      copy[index] = true;
      return copy;
    });
  };


  useEffect(() => {
    if (
      isEditMode &&
      Array.isArray(formData.reference_links) &&
      formData.reference_links.length > 0 &&
      typeof formData.reference_links[0] === "object"
    ) {
      setReferenceLinks(formData.reference_links.map(ref => ({
        id: ref.id,
        link: ref.link,
      })));
    }
  }, [isEditMode, formData.reference_links]);

  const submitReferenceLinks = async (index) => {
    const errors = validateGuidelinesStep(formData, referenceLinks, false);

    if (errors?.reference_links?.[index]) {
      setFormErrors((prev) => ({
        ...prev,
        reference_links: {
          ...(prev.reference_links || {}),
          [index]: errors.reference_links[index],
        },
      }));
      return;
    } else {
      setFormErrors((prev) => {
        const updated = { ...prev };
        if (updated.reference_links) {
          updated.reference_links[index] = "";
        }
        return updated;
      });
    }
    const linkObj = referenceLinks[index];

    if (
      linkEditedFlags[index] &&
      typeof linkObj.link === "string" &&
      linkObj.link.trim() !== ""
    ) {
      try {
        const payload = { link: linkObj.link, barter: offerId };

        if (isEditMode && linkObj.id) {
          await ApiService.put("reference_links", payload, { id: linkObj.id });
        } else if (linkObj.id) {
          await ApiService.put("reference_links", payload, { id: linkObj.id });
        } else {
          const formDataToSend = new FormData();
          const idToSend = isEditMode ? offerId : barterId;
          formDataToSend.append("barter", idToSend);
          formDataToSend.append("link", linkObj.link);

          const response = await ApiService.post("reference_links", formDataToSend);

          if (response && response.data && response.data.id) {
            setReferenceLinks(prev => {
              const updated = [...prev];
              updated[index] = { ...updated[index], id: response.data.id };
              return updated;
            });
          } else if (response && response.id) {
            setReferenceLinks(prev => {
              const updated = [...prev];
              updated[index] = { ...updated[index], id: response.id };
              return updated;
            });
          }
        }

        console.log(`Reference link ${index + 1} submitted`);

        setLinkEditedFlags((prev) => {
          const copy = [...prev];
          copy[index] = false;
          return copy;
        });
      } catch (error) {
        console.error("Failed to submit reference link:", error);
      }
    }
  };

  return (
    <Box>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: '10px', mb: 3 }}>
            <CardContent>
              <SectionTitle>Reference Links</SectionTitle>
              {referenceLinks.map((link, index) => (
                <Grid container spacing={1} alignItems="center" key={index} >
                  <Grid item xs={11}>
                    <ReusableComponent
                      type="text"
                      label={`Reference Link ${index + 1}`}
                      placeholder="Drive link or Instagram / Facebook / YouTube URL"
                      value={link.link}
                      onChange={(value) => handleReferenceLinkChange(index, value)}
                      requireValue={true}
                      marginSize={marginBottom}
                      error={!!formErrors.reference_links?.[index]}
                      helperText={formErrors.reference_links?.[index]}
                      onBlur={() => submitReferenceLinks(index)}
                    />
                  </Grid>
                  <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {referenceLinks.length > 1 && (
                      <IconButton onClick={() => handleRemoveReferenceLink(index)} color="error">
                        <DeleteOutlineIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}

              {referenceLinks.length < 4 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={handleAddReferenceLink}
                  startIcon={<AddBoxOutlinedIcon sx={{ color: '#1976D2' }} />}
                  sx={{ color: "#7A7A7A", textTransform: "none" }}
                >
                  Add more
                </Button>
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: '10px', mb: 3 }}>
            <CardContent>
              <SectionTitle>Brand Contact Person</SectionTitle>
              <Grid item xs={12}>
                <ReusableComponent type="text"
                  label="Contact Name"
                  placeholder={"Enter Name"}
                  value={formData.contact_name}
                  onChange={(value) => handleInputChange("contact_name", value)}
                  error={!!formErrors.contact_name}
                  helperText={formErrors.contact_name}
                  requireValue={true}
                  marginSize={marginBottom}
                />
              </Grid>
              <Grid item xs={12}>
                <ReusableComponent type="text"
                  label="Designation"
                  placeholder="Brand Partnership Manager"
                  value={formData.designation}
                  onChange={(value) => handleInputChange("designation", value)}
                  error={!!formErrors.designation}
                  helperText={formErrors.designation}
                  requireValue={true}
                />
              </Grid>

              <Grid container spacing={2} sx={{ padding: '13px 0px 10px 0px' }}>
                <Grid item xs={6} >

                  <ReusableComponent type="text"
                    label="Email"
                    placeholder={'Enter Email'}
                    value={formData.email}
                    onChange={(value) => handleInputChange("email", value)}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    requireValue={true}
                  // marginSize={marginBottom}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ReusableComponent type="numeric"
                    label="Phone"
                    placeholder={'Enter Phone Number'}
                    value={formData.phone}
                    // onChange={(value) => handleInputChange("phone", value)}
                    onChange={(value) => {
                      const numericValue = value.replace(/\D/g, '').slice(0, 10);
                      handleInputChange("phone", numericValue);
                    }}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                    requireValue={true}
                  // marginSize={marginBottom}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: '10px', mb: 3 }}>
            <CardContent>
              <RichTextEditor
                value={formData.content_do}
                onChange={(value) => handleInputChange("content_do", value)}
                placeholder="Write something ...."
                minHeight={100}
                title="Content Do's (Best Practices)"
                error={!!formErrors.content_do}
                helperText={formErrors.content_do}
                width={{xs:"100%",md:"570px"}}
              />
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: '10px', mb: 3 }}>
            <CardContent>
              <RichTextEditor
                value={formData.content_dont}
                onChange={(value) => handleInputChange("content_dont", value)}
                placeholder="Write something ...."
                minHeight={100}
                title="Content Don'ts (Avoid These)"
                error={!!formErrors.content_dont}
                helperText={formErrors.content_dont}
                width={{xs:"100%",md:"570px"}}
              />
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: '10px', mb: 3 }}>
            <CardContent>
              <RichTextEditor
                value={formData.guidelines}
                onChange={(value) => handleInputChange("guidelines", value)}
                placeholder="• Emphasize product quality
• Mention sustainability
• Highlight unique features
                "
                minHeight={150}
                title='Messaging Guidelines (USP)'
                width={{xs:"100%",md:"570px"}}
                error={!!formErrors.guidelines}
                helperText={formErrors.guidelines}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: '10px', mb: 3 }}>
            <CardContent>
              <SectionTitle>Content Review</SectionTitle>
              <Box display="flex" alignItems="center" gap={1} justifyContent='space-between'>
                <Typography variant="body1">Require Content Approval Before Posting</Typography>
                <Button
                  onClick={() =>
                    handleInputChange("content_review", !formData.content_review)
                  }
                  sx={{ minWidth: 'auto', padding: 0 }}
                >
                  {formData.content_review ? (
                    <ToggleOnIcon sx={{ fontSize: 40, color: '#ED3237' }} />
                  ) : (
                    <ToggleOffIcon sx={{ fontSize: 40, color: 'grey' }} />
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: '10px', mb: 3 }}>
            <CardContent>
              <RichTextEditor
                value={formData.hashtags}
                onChange={(value) => handleInputChange("hashtags", value)}
                placeholder="Write something ...."
                maxLength={100}
                minHeight={100}
                title="Mandatory Hashtags"
                error={!!formErrors.hashtags}
                helperText={formErrors.hashtags}
                required={true}
                width={{xs:"100%",md:"570px"}}
              />
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: '10px', mb: 3 }}>
            <CardContent>
              <RichTextEditor
                value={formData.mentions}
                onChange={(value) => handleInputChange("mentions", value)}
                placeholder="Write something ...."
                maxLength={100}
                minHeight={100}
                title="Mandatory Mentions"
                error={!!formErrors.mentions}
                helperText={formErrors.mentions}
                required={true}
                width={{xs:"100%",md:"570px"}}
              />
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: '10px' }}>
            <CardContent>
              <RichTextEditor
                value={formData.faq}
                onChange={(value) => handleInputChange("faq", value)}
                placeholder="Write something ...."
                maxLength={1000}
                // minHeight={{ xs: 150, md: 400}}
                minHeight={100}
                title="FAQ"
                width={{xs:"100%",md:"570px"}}
                error={!!formErrors.faq}
                helperText={formErrors.faq}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ContentGuidelines
